import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { NATIONALITY_TO_ISO } from "../lib/flags";
import { getCountries, assetUrl } from "../lib/api";

const buildIndex = (countries) => {
    const idx = {};
    for (const c of countries) {
        for (const k of [c.name, c.iso, c.code3, ...(c.aliases || [])]) {
            if (k) idx[String(k).toLowerCase()] = c;
        }
    }
    return idx;
};

// Shape → dimensions + style. rectangle keeps the true flag aspect; the others
// crop to a square footprint (object-cover) then clip. "original" shows the
// WHOLE flag at its natural aspect (object-contain) — no cropping/cut-offs.
const shapeStyle = (shape, size) => {
    if (shape === "original") return { cls: "rounded-[2px]", style: { height: size, width: "auto", maxWidth: size * 2, objectFit: "contain" } };
    const w = shape === "rectangle" ? Math.round(size * 1.5) : size;
    const base = { width: w, height: size, objectFit: "cover" };
    if (shape === "circle") return { cls: "rounded-full", style: base };
    if (shape === "square") return { cls: "rounded-[3px]", style: base };
    if (shape === "triangle") return { cls: "", style: { ...base, clipPath: "polygon(50% 0%, 100% 100%, 0% 100%)" } };
    return { cls: "rounded-[2px]", style: base };
};

/**
 * Universal country flag. Resolves the nationality via the Countries registry
 * (uploaded high-quality image) and falls back to flagcdn by ISO. Shape + a
 * global size scale are driven by the admin Countries settings.
 */
export const Flag = ({ nationality, size = 14, className = "", shape: shapeOverride }) => {
    const { data } = useQuery({ queryKey: ["countries"], queryFn: getCountries, staleTime: 5 * 60 * 1000 });
    const idx = useMemo(() => buildIndex(data?.countries || []), [data]);
    if (!nationality) return null;
    const shape = shapeOverride || data?.flag_shape || "rectangle";
    const scale = (data?.flag_size || 100) / 100;
    const c = idx[String(nationality).toLowerCase()];
    const iso = c?.iso || NATIONALITY_TO_ISO[nationality];
    const src = c?.flag_url ? assetUrl(c.flag_url) : (iso ? `https://flagcdn.com/h40/${iso}.png` : null);
    if (!src) return null;
    const { cls, style } = shapeStyle(shape, Math.round(size * scale));
    return (
        <img
            src={src}
            alt={c?.name || nationality}
            title={c?.name || nationality}
            loading="lazy"
            data-testid={`flag-${iso || "custom"}`}
            className={`inline-block shadow-[0_0_0_1px_rgba(255,255,255,0.08)] ${cls} ${className}`}
            style={style}
        />
    );
};

export default Flag;
